import React, { useEffect, useState } from "react";
import { Plus, Megaphone, ImagePlus } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, fmtDate, EmptyState } from "../components/ui";

export default function Announcements() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", priority: "NORMAL" });
  const [img, setImg] = useState(null);
  const canPost = ["SUPERADMIN", "HR_ADMIN", "MANAGER"].includes(user?.role);

  const load = () => api.get("/announcements").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setImg(null);
    if (f.size > 2 * 1024 * 1024) { alert("Image must be under 2MB"); e.target.value = ""; return; }
    const r = new FileReader();
    r.onload = () => setImg(r.result);
    r.readAsDataURL(f);
  };
  const post = async (e) => {
    e.preventDefault();
    try { await api.post("/announcements", { ...form, image: img || undefined }); setOpen(false); setForm({ title: "", body: "", priority: "NORMAL" }); setImg(null); load(); }
    catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  if (!items) return <Loading />;

  const border = { HIGH: "border-l-amber-500", URGENT: "border-l-red-500", NORMAL: "border-l-accent" };
  return (
    <div>
      <PageHeader title="Announcements" subtitle="Company-wide updates">
        {canPost && <button data-testid="add-announcement" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> Post</button>}
      </PageHeader>
      {items.length === 0 ? <EmptyState icon={Megaphone} title="No announcements" /> : (
        <div className="space-y-4">
          {items.map((a) => (
            <Card key={a.id} className={`overflow-hidden border-l-4 ${border[a.priority] || border.NORMAL}`} data-testid={`announcement-${a.id}`}>
              {a.hasImage && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/announcements/${a.id}/image`} alt={a.title}
                  className="h-44 w-full object-cover" data-testid={`announcement-img-${a.id}`} />
              )}
              <div className="p-6">
                <div className="mb-2 flex items-start justify-between gap-3">
                  <h3 className="font-head text-lg font-bold text-slate-900 dark:text-white">{a.title}</h3>
                  <Badge value={a.priority} />
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">{a.body}</p>
                <p className="mt-3 text-xs text-slate-400">{a.authorName} · {fmtDate(a.createdAt)}</p>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="New Announcement"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="ann-form" className="btn-accent">Post</button></>}>
        <form id="ann-form" onSubmit={post} className="space-y-4">
          <div><label className="label">Title *</label><input required data-testid="ann-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" /></div>
          <div><label className="label">Body *</label><textarea required data-testid="ann-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input h-28 py-2" /></div>
          <div><label className="label">Priority</label>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="input">
              {["NORMAL", "HIGH", "URGENT"].map((p) => <option key={p}>{p}</option>)}
            </select></div>
          <div>
            <label className="label flex items-center gap-1"><ImagePlus size={13} /> Banner Image (optional, shows on the Dashboard hero)</label>
            <input type="file" accept="image/*" data-testid="ann-image" onChange={onFile} className="input !py-1.5 text-sm" />
            {img && <img src={img} alt="preview" className="mt-2 h-24 w-full rounded-lg object-cover" />}
          </div>
        </form>
      </Modal>
    </div>
  );
}
